/**
 * @file auction-filter.ts
 * @description 경매 리스트 필터링 관련 타입 정의
 *
 * BIDIX 경매 리스트 페이지의 필터링 기능을 위한 타입들을 정의합니다.
 * URL 쿼리 파라미터와 동기화되는 필터 상태를 관리합니다.
 *
 * @see {@link /components/auction/FilterBar.tsx} - 필터바 컴포넌트
 * @see {@link /app/list/page.tsx} - 리스트 페이지
 */

export type AuctionRegion = '서울특별시' | '경기도' | '인천광역시' | '부산광역시' | '울산광역시' | '경상남도' | '충청남도' | '세종특별자치시' | '대전광역시' | '강원특별자치시' | '경상북도' | '대구광역시' | '충청북도' | '전라남도' | '전북특별자치도';

export type AuctionPropertyType = '아파트' | '오피스텔' | '빌라' | '다세대' | '주상복합';

export type AuctionSortOrder = '등록순' | '신건순' | '마감임박순';

/**
 * 경매 리스트 필터 상태
 *
 * URL 쿼리 파라미터와 동기화되는 필터링 옵션들입니다.
 */
export interface AuctionFilters {
  /** 지역 필터 (다중 선택 가능) */
  regions: AuctionRegion[];
  /** 매물 유형 필터 (다중 선택 가능) */
  propertyTypes: AuctionPropertyType[];
  /** 정렬 순서 */
  sortOrder: AuctionSortOrder;
}

/**
 * 필터 쿼리 파라미터 타입
 *
 * URL 검색 파라미터로 사용되는 필터 값들입니다.
 * useSearchParams와 연동됩니다.
 */
export interface AuctionFilterParams {
  /** 지역 (콤마로 구분된 문자열, 예: "서울특별시,경기도") */
  regions?: string;
  /** 매물 유형 (콤마로 구분된 문자열, 예: "아파트,빌라") */
  types?: string;
  /** 정렬 순서 */
  sort?: string;
}

/**
 * 필터 옵션 설정
 *
 * 각 필터 타입에 대한 사용 가능한 옵션들을 정의합니다.
 */
export const AUCTION_FILTER_OPTIONS = {
  regions: ['서울특별시', '경기도', '인천광역시', '부산광역시', '울산광역시', '경상남도', '충청남도', '세종특별자치시', '대전광역시', '강원특별자치시', '경상북도', '대구광역시', '충청북도', '전라남도', '전북특별자치도'] as const,
  propertyTypes: ['아파트', '오피스텔', '빌라', '다세대', '주상복합'] as const,
  sortOrders: ['등록순', '신건순', '마감임박순'] as const,
} as const;

/**
 * 필터 표시용 레이블
 *
 * UI에 표시할 때 사용할 짧은 이름들입니다.
 */
export const AUCTION_FILTER_LABELS = {
  regions: {
    '서울특별시': '서울',
    '경기도': '경기',
    '인천광역시': '인천',
    '부산광역시': '부산',
    '울산광역시': '울산',
    '경상남도': '경남',
    '충청남도': '충남',
    '세종특별자치시': '세종',
    '대전광역시': '대전',
    '강원특별자치시': '강원',
    '경상북도': '경북',
    '대구광역시': '대구',
    '충청북도': '충북',
    '전라남도': '전남',
    '전북특별자치도': '전북',
  },
  propertyTypes: {
    '아파트': '아파트',
    '오피스텔': '오피스텔',
    '빌라': '빌라',
    '다세대': '다세대',
    '주상복합': '주상복합',
  },
} as const;

/**
 * 기본 필터 상태
 *
 * 필터가 초기화되었을 때의 기본값입니다.
 */
export const DEFAULT_AUCTION_FILTERS: AuctionFilters = {
  regions: [],
  propertyTypes: [],
  sortOrder: '등록순',
};

/**
 * 필터 유틸리티 함수들
 */
export const AuctionFilterUtils = {
  /**
   * 지역 배열을 쿼리 파라미터 문자열로 변환
   */
  regionsToParam: (regions: AuctionRegion[]): string => regions.join(','),

  /**
   * 쿼리 파라미터 문자열을 지역 배열로 변환
   */
  paramToRegions: (param: string | null): AuctionRegion[] => {
    if (!param) return [];
    return param.split(',').filter((r): r is AuctionRegion =>
      AUCTION_FILTER_OPTIONS.regions.includes(r as AuctionRegion)
    );
  },

  /**
   * 매물 유형 배열을 쿼리 파라미터 문자열로 변환
   */
  typesToParam: (types: AuctionPropertyType[]): string => types.join(','),

  /**
   * 쿼리 파라미터 문자열을 매물 유형 배열로 변환
   */
  paramToTypes: (param: string | null): AuctionPropertyType[] => {
    if (!param) return [];
    return param.split(',').filter((t): t is AuctionPropertyType =>
      AUCTION_FILTER_OPTIONS.propertyTypes.includes(t as AuctionPropertyType)
    );
  },

  /**
   * 정렬 순서를 쿼리 파라미터로 변환
   */
  sortToParam: (sort: AuctionSortOrder): string => sort,

  /**
   * 쿼리 파라미터를 정렬 순서로 변환
   */
  paramToSort: (param: string | null): AuctionSortOrder => {
    if (!param) return DEFAULT_AUCTION_FILTERS.sortOrder;
    return AUCTION_FILTER_OPTIONS.sortOrders.includes(param as AuctionSortOrder)
      ? (param as AuctionSortOrder)
      : DEFAULT_AUCTION_FILTERS.sortOrder;
  },
} as const;
